import { useEffect, useRef, useState } from 'react'

interface UseInViewOptions {
  threshold?: number
  rootMargin?: string
  once?: boolean
}

export function useInView<T extends HTMLElement>({
  threshold = 0.15,
  rootMargin = '0px 0px -8% 0px',
  once = true,
}: UseInViewOptions = {}): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (element === null) return

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }

    const effectiveThreshold =
      element.getBoundingClientRect().height > window.innerHeight * 0.9 ? 0 : threshold

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true)
            if (once) observer.unobserve(entry.target)
          } else if (!once) {
            setInView(false)
          }
        }
      },
      { threshold: effectiveThreshold, rootMargin },
    )

    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [threshold, rootMargin, once])

  return [ref, inView]
}
